/*
 * Aurora Sleep Co. — "Two levers" sleep-onset interactive
 * ------------------------------------------------------
 * Drop-in, dependency-free. Renders a two-toggle explainer showing why
 * one calming herb only solves half the wind-down problem.
 *
 * USAGE
 *   1. Upload as assets/aurora-gaba-levers.js
 *   2. In theme.liquid (before </body>):
 *        <script src="{{ 'aurora-gaba-levers.js' | asset_url }}" defer></script>
 *   3. Place the mount point in your section / custom HTML block:
 *        <div data-aurora-gaba></div>
 *
 * OPTIONS (data attributes on the mount point, all optional)
 *   data-scale="1.5"          overall size multiplier. 1 = base. Clamped 0.75–2.5.
 *   data-max-width="1200px"    caps and centers the widget. Default: fills parent.
 *   data-bg="#12182D"         outer widget background
 *   data-card="#1B2342"       inner card background (meter + levers)
 *   data-flat="true"          make inner cards match the outer background exactly
 *   data-autoplay="false"     disable the scroll-into-view preselect
 *   data-heading="..."        override the eyebrow text
 *
 * Multiple instances on one page are supported.
 */
(function () {
  'use strict';

  var MOUNT = '[data-aurora-gaba]';
  var STYLE_ID = 'aurora-gaba-styles';

  var BARS_WIDE = 48;
  var BARS_NARROW = 24;
  var NARROW_BP = 640;

  var CSS = [
    '.agl{--agl-s:1;',
    '--agl-ink:#12182D;--agl-ink-2:#1B2342;--agl-line:rgba(255,255,255,.10);',
    '--agl-fg:#EDF1F8;--agl-fg-2:#9FB0CC;--agl-calm:#4FD1B0;--agl-calm-2:#2FA98D;',
    '--agl-mid:#E8B44C;--agl-hot:#E2714A;',
    'font-family:inherit;background:var(--agl-ink);color:var(--agl-fg);box-sizing:border-box;',
    'border-radius:calc(18px * var(--agl-s));padding:calc(28px * var(--agl-s)) calc(24px * var(--agl-s))}',
    '.agl *{box-sizing:border-box}',
    '.agl__eyebrow{font-size:calc(12px * var(--agl-s));letter-spacing:.14em;text-transform:uppercase;',
    'color:var(--agl-fg-2);margin:0 0 calc(18px * var(--agl-s))}',
    '.agl__meter{background:var(--agl-ink-2);border-radius:calc(14px * var(--agl-s));',
    'padding:calc(18px * var(--agl-s));margin-bottom:calc(18px * var(--agl-s))}',
    '.agl__meterhead{display:flex;align-items:baseline;justify-content:space-between;',
    'gap:calc(12px * var(--agl-s));margin-bottom:calc(14px * var(--agl-s))}',
    '.agl__meterlabel{font-size:calc(13px * var(--agl-s));color:var(--agl-fg-2)}',
    '.agl__state{font-size:calc(16px * var(--agl-s));font-weight:600;transition:color .45s ease}',
    '.agl__bars{display:flex;align-items:flex-end;gap:calc(4px * var(--agl-s));',
    'height:calc(clamp(150px, 42vw, 190px) * var(--agl-s))}',
    '@media(min-width:640px){.agl__bars{gap:calc(3px * var(--agl-s));',
    'height:calc(clamp(130px, 15vw, 180px) * var(--agl-s))}}',
    '.agl__bar{flex:1;min-width:3px;border-radius:calc(3px * var(--agl-s));',
    'transition:height .55s cubic-bezier(.22,.61,.36,1),background-color .55s ease}',
    '.agl__levers{display:grid;grid-template-columns:1fr;gap:calc(12px * var(--agl-s))}',
    '@media(min-width:640px){.agl__levers{grid-template-columns:1fr 1fr}}',
    '.agl__lever{position:relative;display:block;width:100%;min-height:48px;text-align:left;',
    'padding:calc(16px * var(--agl-s)) calc(18px * var(--agl-s));border-radius:calc(14px * var(--agl-s));',
    'border:1px solid var(--agl-line);background:var(--agl-ink-2);color:inherit;font:inherit;cursor:pointer;',
    'transition:border-color .3s ease,background-color .3s ease}',
    '.agl__lever:hover{border-color:rgba(255,255,255,.24)}',
    '.agl__lever:focus-visible{outline:2px solid var(--agl-calm);outline-offset:3px}',
    '.agl__lever[aria-pressed="true"]{border-color:var(--agl-calm-2);background:rgba(79,209,176,.10)}',
    '.agl__levertop{display:flex;align-items:center;gap:calc(10px * var(--agl-s));',
    'margin-bottom:calc(6px * var(--agl-s))}',
    '.agl__name{font-size:calc(16px * var(--agl-s));font-weight:600;letter-spacing:.01em}',
    '.agl__dose{font-size:calc(12px * var(--agl-s));color:var(--agl-fg-2);font-variant-numeric:tabular-nums}',
    '.agl__pill{margin-left:auto;font-size:calc(11px * var(--agl-s));letter-spacing:.08em;',
    'text-transform:uppercase;padding:calc(3px * var(--agl-s)) calc(9px * var(--agl-s));',
    'border-radius:999px;background:rgba(255,255,255,.07);color:var(--agl-fg-2);transition:all .3s ease}',
    '.agl__lever[aria-pressed="true"] .agl__pill{background:rgba(79,209,176,.18);color:var(--agl-calm)}',
    '.agl__desc{font-size:calc(13.5px * var(--agl-s));line-height:1.55;color:var(--agl-fg-2);margin:0}',
    '.agl__note{font-size:calc(14.5px * var(--agl-s));line-height:1.65;color:var(--agl-fg-2);',
    'margin:calc(18px * var(--agl-s)) 0 0;min-height:3.3em}',
    '.agl__note strong{color:var(--agl-fg);font-weight:600}',
    '.agl--flat .agl__meter,.agl--flat .agl__lever{background:var(--agl-ink)}',
    '.agl__sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;',
    'clip:rect(0 0 0 0);white-space:nowrap;border:0}',
    '@media(prefers-reduced-motion:reduce){.agl__bar,.agl__lever{transition:none}}'
  ].join('');

  var STATES = {
    none: {
      amp: 1,
      raw: '#E2714A',
      state: 'Still racing',
      note: 'Your body is done. Your brain is not. Switch one on.'
    },
    one: {
      amp: 0.6,
      raw: '#E8B44C',
      state: 'Quieter, not quiet',
      note: 'Better — but half the problem is still running. <strong>This is where a single-herb sleep tea leaves you.</strong>'
    },
    both: {
      amp: 0.15,
      raw: '#4FD1B0',
      state: 'Quiet',
      note: 'Both levers, and the room finally goes quiet. One keeps the calming signal in play, the other makes sure it lands. <strong>That is the handoff into sleep.</strong>'
    }
  };

  var LEVERS = [
    {
      key: 'lemonbalm',
      name: 'Lemon balm',
      dose: '1,000 mg',
      desc: 'Helps the calming signal you already have stay in play longer, instead of being cleared away.'
    },
    {
      key: 'magnolia',
      name: 'Magnolia bark',
      dose: '1,000 mg',
      desc: 'Makes the receiving end more responsive, so the same signal lands harder.'
    }
  ];

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }

  function stateFor(count) {
    if (count >= 2) return STATES.both;
    if (count === 1) return STATES.one;
    return STATES.none;
  }

  function clampScale(raw) {
    var n = parseFloat(raw);
    if (isNaN(n)) return 1;
    return Math.min(2.5, Math.max(0.75, n));
  }

  function targetBarCount() {
    return window.innerWidth < NARROW_BP ? BARS_NARROW : BARS_WIDE;
  }

  function Widget(root) {
    this.root = root;
    this.on = {};
    this.bars = [];
    this.barCount = 0;
    this.autoplay = root.getAttribute('data-autoplay') !== 'false';
    this.touched = false;
    this.build();
    this.buildBars();
    this.render();
    this.watch();
    this.bindResize();
  }

  Widget.prototype.build = function () {
    var root = this.root;
    var eyebrow = root.getAttribute('data-heading') || 'Two things go wrong at 11pm';
    var maxWidth = root.getAttribute('data-max-width');
    var bg = root.getAttribute('data-bg');
    var card = root.getAttribute('data-card');

    root.className = (root.className ? root.className + ' ' : '') + 'agl';
    root.style.setProperty('--agl-s', clampScale(root.getAttribute('data-scale')));

    if (bg) root.style.setProperty('--agl-ink', bg);
    if (card) root.style.setProperty('--agl-ink-2', card);
    if (root.getAttribute('data-flat') === 'true') root.classList.add('agl--flat');

    if (maxWidth) {
      root.style.maxWidth = maxWidth;
      root.style.marginLeft = 'auto';
      root.style.marginRight = 'auto';
    }

    var sr = document.createElement('p');
    sr.className = 'agl__sr';
    sr.textContent =
      'Interactive demo. Switching on both lemon balm and magnolia bark together reduces ' +
      'the mental noise level far more than either one alone.';
    root.appendChild(sr);

    var eb = document.createElement('p');
    eb.className = 'agl__eyebrow';
    eb.textContent = eyebrow;
    root.appendChild(eb);

    var meter = document.createElement('div');
    meter.className = 'agl__meter';

    var head = document.createElement('div');
    head.className = 'agl__meterhead';
    var lbl = document.createElement('span');
    lbl.className = 'agl__meterlabel';
    lbl.textContent = 'Mental noise at bedtime';
    this.stateEl = document.createElement('span');
    this.stateEl.className = 'agl__state';
    this.stateEl.setAttribute('aria-live', 'polite');
    head.appendChild(lbl);
    head.appendChild(this.stateEl);
    meter.appendChild(head);

    this.barWrap = document.createElement('div');
    this.barWrap.className = 'agl__bars';
    this.barWrap.setAttribute('aria-hidden', 'true');
    meter.appendChild(this.barWrap);
    root.appendChild(meter);

    var levers = document.createElement('div');
    levers.className = 'agl__levers';
    var self = this;

    LEVERS.forEach(function (cfg) {
      self.on[cfg.key] = false;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'agl__lever';
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('data-key', cfg.key);

      var top = document.createElement('div');
      top.className = 'agl__levertop';

      var name = document.createElement('span');
      name.className = 'agl__name';
      name.textContent = cfg.name;

      var dose = document.createElement('span');
      dose.className = 'agl__dose';
      dose.textContent = cfg.dose;

      var pill = document.createElement('span');
      pill.className = 'agl__pill';
      pill.textContent = 'off';

      top.appendChild(name);
      top.appendChild(dose);
      top.appendChild(pill);

      var desc = document.createElement('p');
      desc.className = 'agl__desc';
      desc.textContent = cfg.desc;

      btn.appendChild(top);
      btn.appendChild(desc);

      btn.addEventListener('click', function () {
        self.touched = true;
        self.on[cfg.key] = !self.on[cfg.key];
        self.render();
      });

      levers.appendChild(btn);
    });

    root.appendChild(levers);

    this.note = document.createElement('p');
    this.note.className = 'agl__note';
    root.appendChild(this.note);
  };

  Widget.prototype.buildBars = function () {
    var want = targetBarCount();
    if (want === this.barCount) return false;

    this.barWrap.innerHTML = '';
    this.bars = [];
    for (var i = 0; i < want; i++) {
      var b = document.createElement('div');
      b.className = 'agl__bar';
      b.style.height = '18%';
      this.barWrap.appendChild(b);
      this.bars.push(b);
    }
    this.barCount = want;
    return true;
  };

  Widget.prototype.bindResize = function () {
    var self = this;
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        if (self.buildBars()) self.render();
      }, 180);
    });
  };

  Widget.prototype.render = function () {
    var count = 0;
    var key;
    for (key in this.on) {
      if (this.on[key]) count++;
    }

    var s = stateFor(count);
    var n = this.bars.length;
    var spread = n < 32 ? 3.1 : 1.7;

    for (var i = 0; i < n; i++) {
      var base = 10 + Math.abs(Math.sin(i * spread) * Math.cos(i * 0.9)) * 88;
      this.bars[i].style.height = (6 + base * s.amp).toFixed(1) + '%';
      this.bars[i].style.backgroundColor = s.raw;
    }

    this.stateEl.textContent = s.state;
    this.stateEl.style.color = s.raw;
    this.note.innerHTML = s.note;

    var self = this;
    var buttons = this.root.querySelectorAll('.agl__lever');
    Array.prototype.forEach.call(buttons, function (btn) {
      var k = btn.getAttribute('data-key');
      var isOn = !!self.on[k];
      btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      btn.querySelector('.agl__pill').textContent = isOn ? 'on' : 'off';
    });
  };

  Widget.prototype.watch = function () {
    if (!this.autoplay) return;
    if (typeof IntersectionObserver === 'undefined') return;

    var self = this;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.disconnect();
          if (self.touched || self.on.lemonbalm) return;
          setTimeout(function () {
            if (self.touched) return;
            self.on.lemonbalm = true;
            self.render();
          }, 500);
        });
      },
      { threshold: 0.4 }
    );
    io.observe(this.root);
  };

  function init() {
    injectStyles();
    var nodes = document.querySelectorAll(MOUNT);
    Array.prototype.forEach.call(nodes, function (n) {
      if (n.getAttribute('data-agl-ready')) return;
      n.setAttribute('data-agl-ready', '1');
      new Widget(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  window.AuroraGabaLevers = { init: init };
})();